:host {
  --sizing-check-size: 6px;
  --sizing-input-size: var(--pine-dimension-sm);
  --sizing-margin-block-start: 6px;

  align-items: center;
  display: flex;
  flex-flow: row wrap;
  position: relative;
}

:host(.is-invalid) {
  // Invalid state only affects error message styling, not the radio itself
  // This allows radio-group to handle error display without red radios
  .pds-radio__message--error {
    color: var(--pine-color-text-message-danger);
  }
}

:host(.has-border) {
  align-items: flex-start;
  border: var(--pine-border);
  border-radius: var(--pine-dimension-125);
  column-gap: var(--pine-dimension-xs);
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: var(--pine-dimension-md);

  // On small screens, ensure full width when in a row layout
  @media (max-width: 767px) {
    box-sizing: border-box;
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
  }

  &:hover {
    border: var(--pine-border-hover);
  }

  &:has(input:checked) {
    box-shadow: 0 0 0 2px var(--pine-color-primary-hover);
  }

  label {
    color: var(--pine-color-text);
    font-weight: var(--pine-font-weight-semi-bold);

    &::after {
      content: "";
      cursor: pointer;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 1;
    }
  }

  // Disabled state for bordered radios
  &:has(input:disabled) {
    background: var(--pine-color-background-container-disabled);
    border-color: var(--pine-color-border-disabled);
    cursor: not-allowed;

    label {
      color: var(--pine-color-text-label-disabled);
      cursor: not-allowed;
    }
  }

  // Invalid state for bordered radios - no red styling, only error message
  &.is-invalid {
    .pds-radio__message--error {
      color: var(--pine-color-text-message-danger);
    }
  }
}

input {
  appearance: none;
  background: var(--pine-color-background-container);
  border: var(--pine-border);
  border-radius: var(--pine-border-radius-full);
  flex: none;
  height: var(--sizing-input-size);
  margin: 0;
  margin-block-start: var(--pine-dimension-025);
  position: relative;
  width: var(--sizing-input-size);

  /* stylelint-disable-next-line selector-no-qualifying-type */
  &.visually-hidden {
    border: 0;
    clip: rect(0 0 0 0);
    clip-path: polygon(0 0, 0 0, 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  &:hover {
    background: var(--pine-color-background-container-hover);
    border: var(--pine-border-hover);
  }

  &:checked {
    background: var(--pine-color-accent);
    border-color: var(--pine-color-accent);

    &:hover {
      background: var(--pine-color-accent-hover);
      border-color: var(--pine-color-accent-hover);
    }

    &::after {
      /* stylelint-disable-next-line pine-design-system/prefer-semantic-tokens */
      background: var(--pine-color-white);
      border-radius: var(--pine-border-radius-full);
      content: "";
      display: block;
      height: var(--sizing-check-size);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: var(--sizing-check-size);
    }
  }

  &:disabled {
    background: var(--pine-color-background-container-disabled);
    border-color: var(--pine-color-border-disabled);
    cursor: not-allowed;

    &:checked {
      background-color: var(--pine-color-accent-disabled);
      border-color: var(--pine-color-border-disabled);

      &::after {
        border-color: var(--pine-color-border-disabled);
      }
    }


  }

  &:focus-visible {
    outline: var(--pine-outline-focus);
  }

}

label {
  display: flex;
  gap: var(--pine-dimension-xs);
}

// Disabled label styling
label:has(input:disabled) {
  color: var(--pine-color-text-label-disabled);
  cursor: not-allowed;
}

// Disabled message styling
:host:has(input:disabled) .pds-radio__message {
  color: var(--pine-color-text-label-disabled);
}

.visually-hidden {
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: polygon(0 0, 0 0, 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.pds-radio__message {
  color: var(--pine-color-text-message);
  flex-basis: 100%; // Ensure message wraps to new line in flex container
  font: var(--pine-typography-body);
  margin-inline-start: var(--pine-dimension-md);
  width: 100%;
}

.pds-radio__message--error {
  display: flex;
  gap: var(--pine-dimension-2xs);

  pds-icon {
    margin-block-start: var(--pine-dimension-025);
  }
}

// Image-based radio styles - matches bordered layout
:host(.has-image) {
  align-items: center;
  border: var(--pine-border);
  border-radius: var(--pine-dimension-125);
  column-gap: var(--pine-dimension-xs);
  display: flex;
  flex: 1;
  padding: var(--pine-dimension-md);

  // On small screens, ensure full width when in a row layout
  @media (max-width: 767px) {
    box-sizing: border-box;
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
  }

  &:hover {
    border: var(--pine-border-hover);
  }

  &:has(input:checked) {
    box-shadow: 0 0 0 2px var(--pine-color-primary-hover);
  }

  label {
    color: var(--pine-color-text);
    display: flex;
    font-weight: var(--pine-font-weight-semi-bold);
    gap: var(--pine-dimension-xs);

    &::after {
      content: "";
      cursor: pointer;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 1;
    }
  }

  .pds-radio__image-container {
    align-items: center;
    align-self: center;
    border: var(--pine-border);
    border-radius: var(--pine-border-radius-full);
    cursor: pointer;
    display: flex;
    flex: none;
    grid-area: image;
    justify-content: center;
    overflow: hidden;
    position: relative;
    transition: all var(--pine-motion-duration-base) ease;

    &:hover {
      border-color: var(--pine-color-border-hover);
    }

    .pds-radio__image {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }

  .pds-radio__message {
    grid-area: message;
    margin-block-start: 0;
    margin-inline-start: 0;
  }

  // Disabled state
  &.is-disabled {
    background: var(--pine-color-background-container-disabled);
    border-color: var(--pine-color-border-disabled);
    cursor: not-allowed;

    .pds-radio__image-container {
      border-color: var(--pine-color-border-disabled);
      cursor: not-allowed;
      opacity: 0.5;

      &:hover {
        border-color: var(--pine-color-border-disabled);
      }
    }

    label {
      color: var(--pine-color-text-label-disabled);
      cursor: not-allowed;
    }

    .pds-radio__message {
      color: var(--pine-color-text-label-disabled);
    }
  }

  // Invalid state - no red styling, only error message
  &.is-invalid {
    .pds-radio__message--error {
      color: var(--pine-color-text-message-danger);
    }
  }
}
